/**
 * Converts a base64url-encoded string (RFC 4648 §5) to a Uint8Array.
 *
 * The WebAuthn / Fido2NetLib backend emits binary fields (challenge,
 * allowCredentials[].id, user.id) as base64url. The browser's `atob` only
 * handles standard base64 (`+`/`/`/`=`), so plain `atob` throws
 * InvalidCharacterError on base64url input that contains `-` or `_`.
 *
 * @param {string} base64UrlStr - base64url string, may or may not have `=` padding.
 * @returns {Uint8Array}
 */
const BASE64_BLOCK_SIZE = 4;

const base64UrlToUint8Array = base64UrlStr => {
  const base64 = base64UrlStr.replace(/-/g, '+').replace(/_/g, '/');
  const remainder = base64.length % BASE64_BLOCK_SIZE;
  const padLength = remainder === 0 ? 0 : BASE64_BLOCK_SIZE - remainder;
  const padded = base64.padEnd(base64.length + padLength, '=');
  const binary = atob(padded);

  return Uint8Array.from(binary, c => c.charCodeAt(0));
};

/**
 * Converts a Uint8Array (or ArrayBuffer) to a base64url string (RFC 4648 §5),
 * without padding.
 *
 * The WebAuthn / Fido2NetLib backend expects binary response fields (rawId,
 * authenticatorData, clientDataJSON, signature, attestationObject, userHandle) encoded
 * as base64url, not standard base64.
 *
 * @param {Uint8Array | ArrayBuffer} bytes
 * @returns {string}
 */
// Encode in chunks: String.fromCharCode(...arr) spreads every byte as a separate argument, which
// overflows the call-stack arg ceiling on multi-KB buffers (the enrollment attestationObject) and
// throws RangeError — surfacing as a generic "Could not set up the passkey". 0x8000 is well under
// the spread limit in every engine.
const BASE64_ENCODE_CHUNK_SIZE = 0x8000;

const uint8ArrayToBase64Url = bytes => {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

  const chunkCount = Math.ceil(arr.length / BASE64_ENCODE_CHUNK_SIZE);
  const binary = Array.from({ length: chunkCount }, (_, index) => {
    const offset = index * BASE64_ENCODE_CHUNK_SIZE;

    return String.fromCharCode(...arr.subarray(offset, offset + BASE64_ENCODE_CHUNK_SIZE));
  }).join('');

  const base64 = btoa(binary);

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export { base64UrlToUint8Array, uint8ArrayToBase64Url };
